import { Tooltip } from '@material-ui/core';
import { Undo } from '@material-ui/icons';
import { MaterialTableProps } from 'material-table';
import React, { Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import TableLayout from '../../../../components/Layout/TableLayout';
import { formatNumber } from '../../../../helpers/formatNumber';
import { tFormKey, tKey } from '../../../../helpers/translate';
import { rollbackSentInvoiceAction } from '../../../../redux/accounting/actions';
import { InvoiceListItem } from '../../../../redux/accounting/definitions';
import { selectLoading } from '../../../../redux/accounting/selectors';
import { showSnackbar } from '../../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../../redux/FeedbackAPI/definitions';
import { selectUserCan } from '../../../../redux/permissions/selectors';
import { AppState } from '../../../../redux/root-reducer';
import StateCode from '../../../../types/Invoices/StateCode';
import EntityMenuRight from '../../../Entities/EntityMenuRight/EntityMenuRight';
import AccountingSubmenu from '../../AccountingSubmenu';
import useInvoiceList from '../hooks/useInvoiceList';
import useRollbackInvoiceModal from '../hooks/useRollbackInvoiceModal';
import InvoicesList from './InvoicesList';

const TransitionModal = React.lazy(() => import('../../../../components/Modal/TransitionModal'));

interface PaidInvoicesListProps {
  accountingLoading: boolean;
  canRollbackInvoices: boolean;
  rollbackSentInvoice: (invoice_id: number, stateCode: StateCode) => void;
  showSnackbar: (message: string, severity: SeveritySnackbar) => void;
}

const PaidInvoicesList: React.FC<PaidInvoicesListProps> = ({
  accountingLoading,
  canRollbackInvoices,
  rollbackSentInvoice,
  showSnackbar,
}) => {
  const { loading, invoices, errorListMessage, stateFilters } = useInvoiceList('paid', accountingLoading);
  const { rollabackModal, openRollbackModal, closeRollbackModal } = useRollbackInvoiceModal();

  useEffect(() => {
    if (errorListMessage) {
      showSnackbar(errorListMessage, 'error');
    }
  }, [errorListMessage, showSnackbar]);

  const actionIcons = {
    Undo: () => <Undo style={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
  };

  const actions = [];

  if (canRollbackInvoices) {
    actions.push((rowData: InvoiceListItem) => ({
      icon: actionIcons.Undo,
      tooltip: tFormKey('Retroceder'),
      onClick: () => openRollbackModal(rowData),
    }));
  }

  const tableData: MaterialTableProps<InvoiceListItem> = {
    title: tFormKey('paidInvoicesList.title'),
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        render: rowData => (
          <Tooltip title={rowData.reference_number}>
            <span>{rowData.reference_number}</span>
          </Tooltip>
        ),
        //@ts-ignore
        width: '110px',
      },
      {
        field: 'entity_name',
        title: tKey('Proveedor'),
        render: rowData => (
          <Tooltip title={rowData.entity_name}>
            <span>{rowData.entity_name}</span>
          </Tooltip>
        ),
        //@ts-ignore
        width: '280px',
      },
      {
        field: 'invoice_date',
        title: tFormKey('Fecha'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.invoice_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'due_date',
        title: tFormKey('Vencimiento'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.due_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'total_amount',
        title: tKey('Total'),
        type: 'numeric',
        render: (rowData: InvoiceListItem) => <div>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>,
        //@ts-ignore
        width: '110px',
      },
      {
        field: 'state',
        title: tKey('Estado'),
        //@ts-ignore
        // width: '120px',
        lookup: stateFilters,
        //defaultFilter: !showToPay ? ['Pendiente', 'Aceptada'] : undefined,
      },
    ],
    actions,
    data: invoices,
    options: {
      tableLayout: 'fixed',
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
    },
  };

  tableData.isLoading = loading || accountingLoading;

  const rightSubmenu = <EntityMenuRight actionButtons={[]} />;

  return (
    <TableLayout
      leftSubmenu={<AccountingSubmenu selected={'paid_invoices'} submenu={tKey('Facturas servicios')} />}
      rightSubmenu={rightSubmenu}
    >
      <InvoicesList tableData={tableData} downloadAction />
      <Suspense fallback={<></>}>
        <TransitionModal
          view={rollabackModal.view}
          handleClose={() => {
            closeRollbackModal();
          }}
          bodyModal={`${tFormKey('rollback.invoice.modal.pre')} ${rollabackModal.reference_number} ${tFormKey(
            'rollback.invoice.modal.post',
          )}`}
          buttonOk={tKey('Sí')}
          buttonKo={tKey('No')}
          title={`${tFormKey('Retroceder')}: ${rollabackModal.reference_number} - ${rollabackModal.entity_name}`}
          handleYes={() => {
            rollbackSentInvoice(rollabackModal.invoice_id, rollabackModal.stateCode);
            closeRollbackModal();
          }}
        />
      </Suspense>
    </TableLayout>
  );
};

const mapStateToProps = (state: AppState) => ({
  accountingLoading: selectLoading(state),
  showToPay: selectUserCan(state)('allow_invoice_to_accounting'),
  canRollbackInvoices: selectUserCan(state)('allow_rollback_paid_invoice'),
});

const mapDispatchToProps = (dispatch: any) => ({
  rollbackSentInvoice: (invoice_id: number, stateCode: StateCode) =>
    dispatch(rollbackSentInvoiceAction(invoice_id, stateCode)),
  showSnackbar: (message: string, severity: SeveritySnackbar): void => dispatch(showSnackbar(message, severity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaidInvoicesList);
